"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Navbar } from "@/components/home";
import { Button } from "@/components/ui/button";
import type { Locale } from "@/i18n/config";
import type { Dictionary } from "@/i18n/get-dictionary";
import { isValidLocale, useTranslations } from "@/i18n";

function useSafeTranslations(): Dictionary | null {
  try {
    return useTranslations();
  } catch {
    return null;
  }
}

const CLOSED_COPY: Record<
  string,
  { title: string; body: string; backHome: string }
> = {
  en: {
    title: "Registration Closed",
    body: "Registration for Youth Revival Summit 2026 has closed. Reach out to your youth leader for registration.",
    backHome: "Back Home",
  },
  fr: {
    title: "Inscriptions fermées",
    body: "Les inscriptions pour le Youth Revival Summit 2026 sont maintenant fermées. Communiquez avec votre responsable de jeunesse pour vous inscrire.",
    backHome: "Retour à l'accueil",
  },
  es: {
    title: "Registro cerrado",
    body: "El registro para Youth Revival Summit 2026 ha cerrado. Comunícate con tu líder de jóvenes si deseas participar.",
    backHome: "Volver al inicio",
  },
};

export default function AYACRegisterPage() {
  const pathname = usePathname();
  const prefix = pathname.split("/")[1];
  const locale: Locale = isValidLocale(prefix) ? (prefix as Locale) : "en";
  const dictionary = useSafeTranslations();
  const copy = CLOSED_COPY[locale] ?? CLOSED_COPY.en;

  return (
    <main className="min-h-screen bg-[#F8F7F4] dark:bg-black">
      <Navbar locale={locale} translations={dictionary?.nav} />
      <section className="flex min-h-screen items-center justify-center px-6 pb-20 pt-32 md:pt-40">
        <div className="w-full max-w-lg text-center">
          <h1 className="mb-4 font-display text-4xl font-bold text-black dark:text-white md:text-5xl">
            {copy.title}
          </h1>
          <p className="mb-8 text-lg leading-relaxed text-zinc-500 dark:text-zinc-400">
            {copy.body}
          </p>
          <Button
            variant="outline"
            asChild
            className="h-12 rounded-full border-black/10 px-6 dark:border-white/10"
          >
            <Link href={`/${locale}`}>{copy.backHome}</Link>
          </Button>
        </div>
      </section>
    </main>
  );
}
